import { useState, useEffect } from 'react';
import api from '../Services/api';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, AreaChart, Area, PieChart, Pie
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPhone, faCheckCircle, faUserCheck, faFileLines, 
    faGraduationCap, faChartPie, faArrowTrendUp, faClock
} from '@fortawesome/free-solid-svg-icons';
import "../styles/leads.css"; // Reuse some CRM styles

export default function SelfAnalysis() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await api.get(`crm/analytics/?period=${period}`);
                setData(res.data);
            } catch (err) {
                console.error("Failed to fetch analysis", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [period]);

    if (loading) return <div className="p-loading">Analyzing your performance data...</div>;
    if (!data) return <div>Failed to load analysis.</div>;

    const { personal = {}, kpis = {}, leadsByStatus = [] } = data;
    const leads_by_status = leadsByStatus;

    return (
        <div className="p-dashboard-root" style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
            <header style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>My Performance Analysis</h1>
                    <p style={{ color: '#64748b', fontSize: '14px' }}>Real-time tracking of your daily and cumulative metrics</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {['today', 'week', 'month'].map(p => (
                        <button 
                            key={p}
                            onClick={() => setPeriod(p)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                background: period === p ? '#3b82f6' : 'white',
                                color: period === p ? 'white' : '#64748b',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600',
                                textTransform: 'capitalize'
                            }}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </header>

            {/* Quick Metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                <MetricCard icon={faPhone} color="#3b82f6" label="Calls Made" val={personal.calls_today} sub="Total calls in period" />
                <MetricCard icon={faCheckCircle} color="#10b981" label="Connected" val={personal.connected_today} sub={`${(personal.connected_today/personal.calls_today*100 || 0).toFixed(0)}% Connect Rate`} />
                <MetricCard icon={faUserCheck} color="#22c55e" label="Interested" val={personal.interested_today} sub="New leads today" />
                <MetricCard icon={faFileLines} color="#f59e0b" label="Form Filled" val={personal.form_filled_today} sub="Conversion step" />
                <MetricCard icon={faGraduationCap} color="#a855f7" label="Admissions" val={personal.admission_done_today} sub="Final Goal" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
                {/* Performance Chart */}
                <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                    <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '700' }}><FontAwesomeIcon icon={faArrowTrendUp} style={{ marginRight: '8px', color: '#3b82f6' }} /> Status Conversion Funnel</h3>
                    </div>
                    <div style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[
                                { name: 'Connected', val: personal.connected_today, fill: '#3b82f6' },
                                { name: 'Interested', val: personal.interested_today, fill: '#22c55e' },
                                { name: 'Form Filled', val: personal.form_filled_today, fill: '#f59e0b' },
                                { name: 'Admission', val: personal.admission_done_today, fill: '#a855f7' }
                            ]}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                                <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                <Bar dataKey="val" radius={[4, 4, 0, 0]} barSize={50}>
                                    { [0,1,2,3].map((entry, index) => (
                                        <Cell key={`cell-${index}`} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Status Breakdown */}
                <div style={{ background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '20px' }}><FontAwesomeIcon icon={faChartPie} style={{ marginRight: '8px', color: '#f59e0b' }} /> My Leads Distribution</h3>
                    <div style={{ height: '240px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={leads_by_status}
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="count"
                                    nameKey="name"
                                >
                                    {leads_by_status.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        {leads_by_status.slice(0, 4).map(s => (
                            <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                                <span style={{ color: '#64748b', display: 'flex', alignItems: 'center' }}>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.color, marginRight: '8px' }} />
                                    {s.name}
                                </span>
                                <span style={{ fontWeight: '600' }}>{s.count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Lead Performance Table */}
            <div style={{ marginTop: '24px', background: 'white', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '800' }}>Calls Made per Lead</h3>
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Top 15 Most Contacted</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '1px solid #f1f5f9' }}>
                                <th style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase' }}>Lead Name</th>
                                <th style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase' }}>Status</th>
                                <th style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase' }}>Sub Status</th>
                                <th style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', textAlign: 'center' }}>Total Calls</th>
                                <th style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase' }}>Last Contact</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.leadPerformance || []).map((l, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '700' }}>{l.name}</td>
                                    <td style={{ padding: '12px' }}>
                                        <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: '#f1f5f9', color: '#475569' }}>
                                            {l.status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>{l.sub_status}</td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', background: l.calls > 5 ? '#dcfce7' : '#f1f5f9', color: l.calls > 5 ? '#15803d' : '#475569', fontWeight: '800', fontSize: '12px' }}>
                                            {l.calls}
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '13px', color: '#94a3b8' }}>{l.last_contact}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={{ marginTop: '24px', background: 'linear-gradient(90deg, #1e293b, #0f172a)', padding: '24px', borderRadius: '16px', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>Performance Analytics</h4>
                    <p style={{ opacity: 0.7, fontSize: '14px' }}>Real-time tracking of your activities and conversion metrics.</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', opacity: 0.5, marginBottom: '8px' }}><FontAwesomeIcon icon={faClock} /> Analytics Status</div>
                    <span style={{ padding: '6px 12px', borderRadius: '20px', background: '#22c55e', fontSize: '12px', fontWeight: '700' }}>SYNCED & LIVE</span>
                </div>
            </div>
        </div>
    );
}

function MetricCard({ icon, color, label, val, sub }) {
    return (
        <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${color}15`, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FontAwesomeIcon icon={icon} />
                </div>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>{label}</span>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a' }}>{val}</div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{sub}</div>
        </div>
    );
}
